/**
 * The format PRISM Graphics starts a graphic in (1.2.0): the sequence's frame size and frame rate when the editor is
 * opened from a sequence (New graphic in the bin's Graphics tab, Edit graphic on a clip), e.g. a vertical 9:16
 * sequence at 25 fps gives 1080×1920 at 25/1, (PRISM Graphics 1.1.0 and later start a blank graphic at
 * that size). Without a sequence (the Graphics area, an asset's page), or with a format the
 * bridge would refuse, it is 1920×1080 at 25 fps.
 *
 * 1.7.0: a multi-format sequence also sends its other formats as `defaults.layouts` (bridge 1.1.0), so a new graphic
 * starts with one layout per format; editorLayoutsFor lists them, the primary first, ids as the OGraf layout
 * contract's reduced ratios.
 */
import { aspectLabel, formatIdLabel, type SequenceFormat } from '@prism/contracts';
import type { EditorFormat } from './editorHost';

export const DEFAULT_EDITOR_FORMAT: EditorFormat = { width: 1920, height: 1080, frameRate: { num: 25, den: 1 } };

/** The bridge's limits on host:init defaults: whole pixels up to 16384, and a frame rate of positive integers. */
const pixels = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n > 0 && n <= 16384;
const positive = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n > 0;

/** The further layouts host:init may list (bridge 1.1.0 MAX_DEFAULT_LAYOUTS). */
const MAX_LAYOUTS = 8;

/**
 * What a sequence (or anything with its frame) gives the editor; anything missing or invalid in the frame falls back
 * as a whole. 1.7.0 (bridge 1.1.0): a sequence with formats also gives `layouts`, its other formats' sizes (the frame
 * is the primary layout), so a new graphic starts with one layout per format. A sequence without formats, and no
 * sequence at all (the Graphics area), give no layouts: the graphic has the one layout of the frame, as before
 * (Mikael 2026-10-28: not every sequence or graphic needs several aspect ratios). Defaults made here can be passed in
 * again (GraphicsEditor re-normalises them): their `layouts` are checked and kept.
 */
export function editorDefaultsFor(sequence?: {
  width?: number | null; height?: number | null; frameRate?: { num: number; den: number } | null;
  formats?: SequenceFormat[] | null; layouts?: Array<{ width: number; height: number }> | null;
} | null): EditorFormat {
  if (!sequence) return DEFAULT_EDITOR_FORMAT;
  const { width, height, frameRate } = sequence;
  if (!pixels(width) || !pixels(height) || !frameRate || !positive(frameRate.num) || !positive(frameRate.den)) return DEFAULT_EDITOR_FORMAT;
  const out: EditorFormat = { width, height, frameRate: { num: frameRate.num, den: frameRate.den } };
  if (!BRIDGE_TAKES_LAYOUTS) return out;
  const asked = sequence.formats?.length ? (editorLayoutsFor(sequence) ?? []).slice(1) : (sequence.layouts ?? []);
  const primaryId = formatIdLabel({ width, height });
  const seen = new Set([primaryId]);
  const layouts: Array<{ width: number; height: number }> = [];
  for (const l of asked) {
    if (!pixels(l?.width) || !pixels(l?.height)) continue;
    const id = formatIdLabel(l);
    if (seen.has(id) || layouts.length >= MAX_LAYOUTS) continue;
    seen.add(id);
    layouts.push({ width: l.width, height: l.height });
  }
  return layouts.length ? { ...out, layouts } : out;
}

/** One layout a new graphic starts with (the OGraf layout contract: id a reduced ratio, `16x9`; [0] the primary). */
export interface EditorLayout { id: string; label: string; width: number; height: number }

/**
 * Whether the vendored bridge takes host:init `defaults.layouts`: yes since @prism-graphics/bridge 1.1.0 (PRISM
 * Graphics 1.2.0, vendored in PRISM 1.7.0). An editor before 1.2.0 accepts the field and ignores it.
 */
export const BRIDGE_TAKES_LAYOUTS = true;

/**
 * The layouts of a multi-format sequence for the editor (1.7.0): the primary first, then each format, in the
 * sequence's order; null for a sequence without formats (the editor starts with one layout of the frame size).
 */
export function editorLayoutsFor(sequence?: { width?: number | null; height?: number | null; formats?: SequenceFormat[] | null } | null): EditorLayout[] | null {
  if (!sequence?.formats?.length || !pixels(sequence.width) || !pixels(sequence.height)) return null;
  const primary = { width: sequence.width, height: sequence.height };
  const out: EditorLayout[] = [{ id: formatIdLabel(primary), label: aspectLabel(primary), ...primary }];
  for (const f of sequence.formats) {
    if (!pixels(f.width) || !pixels(f.height)) continue;
    const id = formatIdLabel(f);
    if (out.some((l) => l.id === id)) continue;
    out.push({ id, label: f.label?.trim() || aspectLabel(f), width: f.width, height: f.height });
  }
  return out.length > 1 ? out : null;
}
